import { endpoints } from '@/services/endpoints'
import { post } from '@/services/http'
import { openSseStream } from '@/services/sse'

/**
 * Asking the copilot something, which is TWO requests and not one.
 *
 * `POST /copilot/turns` answers **201 with the created row**. It does not stream.
 * The answer arrives on `GET /copilot/turns/{id}/stream`, opened afterwards
 * against the id that POST returned.
 *
 * This file used to post a question and read SSE off the response, and carried a
 * long argument for why `fetch` rather than `EventSource` — a question in a body
 * needs a POST. Both halves of that are now wrong: the body goes to a JSON
 * endpoint, and the stream is a GET whose only credential is `?token=`. The
 * argument is kept nowhere, because a rewritten file that keeps the reasoning for
 * the shape it no longer has is worse than one with no comment at all.
 *
 * ── Nothing is lost in the gap between the two requests ───────────────────
 * The obvious worry about create-then-stream is that the engine starts work the
 * moment the row lands and the first frames are gone before anyone is listening.
 * They are not: `create_turn` registers the turn's queue immediately after the
 * flush that assigns its id, before it has even answered, and chunks accumulate
 * there whether a reader is attached or not. So the gap costs latency, not
 * content.
 *
 * It is still kept as narrow as it can be — no await, no render, nothing between
 * the POST resolving and the GET going out — because that queue lives in the
 * server's memory and is one process's worth of goodwill, not a durable log.
 */

/**
 * Create the turn. Rejects with `ApiError`.
 *
 * A 400 naming `session_id` (see `isPendingTurnRefusal`) is what the server raises while this session's last
 * question has no answer against it — see `findPendingTurn`, which is what a caller
 * should reach for on it. It is not an edge case: an ordinary failed turn leaves a
 * session in that state permanently.
 */
export async function createCopilotTurn({ sessionId, message, metadata, signal }) {
  return post(
    endpoints.copilot.turns,
    { copilot_session_id: sessionId, message, metadata },
    { signal, meta: { ai: true } },
  )
}

/**
 * Attach to a turn's answer. Resolves when the stream ends.
 *
 * `auth: 'query'` is not a preference. The route depends on
 * `get_current_user_from_query_token`, which declares `token` with no default —
 * there is no header path to choose instead, and the one-shot 401 refresh and
 * retry are already inside `openSseStream`.
 *
 * Deliberately NOT `createSharedSseConnection`. That one reconnects on a clean end,
 * which is right for a resource stream and wrong here twice over: a turn is
 * answered once, and the server drops its queue the moment the reader detaches —
 * so a reconnect finds nothing and is told `stream unavailable`, for ever, on a
 * backoff. A turn stream is single-shot and non-resumable, and the honest way to
 * recover a dropped one is to re-read the session's turns.
 */
export async function streamCopilotTurn({ turnId, signal, onEvent, onOpen }) {
  await openSseStream({
    path: endpoints.copilot.turnStream(turnId),
    method: 'GET',
    auth: 'query',
    signal,
    onEvent,
    onOpen,
  })
}

/**
 * The two together.
 *
 * `onTurnCreated` fires between them, synchronously, and a caller must do nothing
 * in it but record the id — an await there re-opens the gap this shape exists to
 * close. It is separate from the return value because the caller needs the id
 * while the stream is still running, not after it has finished.
 *
 * The halves stay exported on their own because the pending-turn path streams a turn it
 * FOUND rather than one it created, and re-creating one there is exactly the
 * mistake that path exists to avoid.
 */
export async function askCopilotTurn({
  sessionId,
  message,
  metadata,
  signal,
  onEvent,
  onOpen,
  onTurnCreated,
}) {
  const turn = await createCopilotTurn({ sessionId, message, metadata, signal })
  onTurnCreated?.(turn)
  await streamCopilotTurn({ turnId: turn.id, signal, onEvent, onOpen })
  return turn
}
